import { ChangeDetectorRef, inject, Injectable } from '@angular/core';
import { BsApiService, BsChatService, GenericResponseCode, SimpleGlobalModalService } from '@brightside-web/desktop/data-access/core-services';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { CardDetailsPayload, ChangePinPayload, LockCardPayload } from './models/debit-card.models';
import { Router } from '@angular/router';
import { NewSpendingAccountService } from '@brightside-web/desktop/feature/spending-account';
import { NewSpendingCard } from '@brightside-web/desktop/feature/financial-accounts-core';
import { filter, finalize, take } from 'rxjs/operators';
import { ToastService } from '@brightside/brightside-ui-services';
import { TranslateService } from '@ngx-translate/core';
@Injectable()
export class DebitCardService {
  sub = new Subscription();

  apiName = 'api-mobile';
  changePinToast = 'DEBIT_CARD_CHANGE_PIN_SAVED';
  cardLockFailedToast = 'CARD_LOCK_TOAST_FAILED';
  cardUnlockFailedToast = 'CARD_UNLOCK_TOAST_FAILED';
  cardLockSuccessToast = 'CARD_LOCK_TOAST_SUCCESS';
  cardUnlockSuccessToast = 'CARD_UNLOCK_TOAST_SUCCESS';
  accountId: string;

  private _bsChatSvc = inject(BsChatService);

  private _shipProcessing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly shipProcessing: Observable<boolean> = this._shipProcessing.asObservable();

  private _verifyProcessing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly verifyProcessing: Observable<boolean> = this._verifyProcessing.asObservable();

  private _reissueProcessing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly reissueProcessing: Observable<boolean> = this._reissueProcessing.asObservable();

  private _cachedCardDetails: NewSpendingCard | undefined;

  private _currentCardDetails: Subject<NewSpendingCard | undefined> = new Subject<NewSpendingCard | undefined>();
  public readonly currentCardDetails: Observable<NewSpendingCard | undefined> = this._currentCardDetails.asObservable();

  private _changePinProcessing$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly changePinProcessing$: Observable<boolean> = this._changePinProcessing$.asObservable();

  private _cardLockStatus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly cardLockStatus$: Observable<boolean> = this._cardLockStatus$.asObservable();

  private _lockCardToggleLabelKey$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public readonly lockCardToggleLabelKey$: Observable<string> = this._lockCardToggleLabelKey$.asObservable();

  private _debitCardHelpModalShown: boolean = false;

  constructor(
    private _bsApi: BsApiService,
    private _globalModalSvc: SimpleGlobalModalService,
    private _spendingAccountSvc: NewSpendingAccountService,
    private _router: Router,
    private _toastService: ToastService,
    private _translateService: TranslateService
  ) {
    this.sub.add(
      this._spendingAccountSvc.currentAccountInfo.pipe().subscribe((account) => {
        this._currentCardDetails.next(account?.card);
        if (account?.card) {
          this._cachedCardDetails = account.card;
          const cardLocked = account.card?.lockTypeCode !== 'UNL';
          this._cardLockStatus$.next(cardLocked);
          this._lockCardToggleLabelKey$.next(cardLocked ? 'LOCK_CARD_TOGGLE_ON' : 'LOCK_CARD_TOGGLE_OFF');
        }
      })
    );
  }

  getCurrentCard(force = false) {
    this._spendingAccountSvc.getSpendingAccounts(force);
  }

  /**
   * Runs `action` once a card with `cardId` is available. Refreshes spending accounts when cache is missing `cardId`.
   */
  private _runWithCardId(action: (card: NewSpendingCard) => void): void {
    if (this._cachedCardDetails?.cardId) {
      action(this._cachedCardDetails);
      return;
    }
    this.currentCardDetails
      .pipe(
        filter((card): card is NewSpendingCard => !!card?.cardId),
        take(1)
      )
      .subscribe((card) => {
        this._cachedCardDetails = card;
        action(card);
      });
    this.getCurrentCard(true);
  }

  verifyCard(cardNumber: number) {
    this._verifyProcessing.next(true);
    this._runWithCardId((card) => {
      this._bsApi
        .post<CardDetailsPayload>(this.apiName, '/accounts/card/verify', {
          cardId: card.cardId,
          lastFourDigits: cardNumber,
        })
        .subscribe((verifyResp) => {
          if (verifyResp.result.code === GenericResponseCode.SERVICE_OK) {
            this._router.navigate(['accounts', 'spending', 'debit-card', 'change-pin']), {state: {flow: 'activate'}};
            this.changePinToast = 'DEBIT_CARD_ACTIVATED';
            setTimeout(() => {
              this.changePinToast = 'DEBIT_CARD_CHANGE_PIN_SAVED';
            }, 10000);
          } else {
            this._globalModalSvc.createGenericErrorModal('debit_card_activate_error');
          }
          this._verifyProcessing.next(false);
        });
    });
  }

  shipCard(reissue: boolean = false) {
    this._shipProcessing.next(true);
    const _shipCardErrorModal = () => {
      this._globalModalSvc.createGenericErrorModal(
        'ship_debit_card_failed',
        () => {
          this._globalModalSvc.clearModal();
        },
        'TRY_AGAIN',
        () => {
          this._router.navigate(['accounts', 'spending', 'dashboard']);
          this._globalModalSvc.clearModal();
        }
      );
    };
    const post = (requestBody: { cardId: string } | { accountId: string }) => {
      const postPath = reissue ? '/accounts/card/reissue' : '/accounts/card';
      this._bsApi
        .post<CardDetailsPayload>(this.apiName, postPath, requestBody)
        .pipe(finalize(() => this._shipProcessing.next(false)))
        .subscribe({
          next: (resp) => {
            if (resp.result.code === GenericResponseCode.SERVICE_OK) {
              this._router.navigate(['accounts', 'spending', 'debit-card', 'shipping', 'confirmation'], {
                state: { flow: reissue ? 'reissue' : 'normal' },
              });
            } else {
              _shipCardErrorModal();
            }
          },
          error: () => {
            _shipCardErrorModal();
          },
        });
    };

    if (reissue) {
      this._runWithCardId((card) => post({ cardId: card.cardId }));
    } else {
      post({ accountId: this.accountId });
    }
  }

  sendPhysicalCard(): void {
    this._shipProcessing.next(true);
    this._runWithCardId((card) => {
      this._bsApi
        .post(this.apiName, '/accounts/card/order-physical', { cardId: card.cardId })
        .pipe(finalize(() => this._shipProcessing.next(false)))
        .subscribe({
          next: (resp) => {
            if (resp.result.code === GenericResponseCode.SERVICE_OK) {
              this._router.navigate(['accounts', 'spending', 'dashboard']);
            } else {
              this._showOrderPhysicalCardRetryModal();
            }
          },
          error: () => {
            this._showOrderPhysicalCardRetryModal(false);
          },
        });
    });
  }

  /**
   * Reissues a digital-only card (POST accounts/card/reissue). On success navigates to digital confirmation screen.
   * Use when the user has only a digital card and is replacing it (report lost flow).
   */
  reissueDigitalCard(): void {
    this._runWithCardId((card) => {
      this._reissueProcessing.next(true);
      this._bsApi
        .post<CardDetailsPayload>(this.apiName, '/accounts/card/reissue', {
          cardId: card.cardId,
        })
        .subscribe({
          next: (resp) => {
            this._reissueProcessing.next(false);
            if (resp.result.code === GenericResponseCode.SERVICE_OK) {
              this._spendingAccountSvc.getSpendingAccounts(true);
              this._router.navigate(['accounts', 'spending', 'debit-card', 'digital-confirmation']);
            } else {
              this._globalModalSvc.createGenericErrorModal(
                'ship_debit_card_failed',
                () => this._globalModalSvc.clearModal(),
                'OK',
                () => {
                  this._router.navigate(['accounts', 'spending', 'dashboard']);
                  this._globalModalSvc.clearModal();
                }
              );
            }
          },
          error: () => {
            this._reissueProcessing.next(false);
            this._globalModalSvc.createGenericErrorModal(
              'ship_debit_card_failed',
              () => this._globalModalSvc.clearModal(),
              'OK',
              () => {
                this._router.navigate(['accounts', 'spending', 'dashboard']);
                this._globalModalSvc.clearModal();
              }
            );
          },
        });
    });
  }

  /**
   * Orders a digital card (POST accounts/card). On success navigates to digital confirmation screen.
   */
  orderDigitalCard(): void {
    if (!this.accountId) {
      this._router.navigate(['accounts', 'spending', 'dashboard']);
      return;
    }
    this._shipProcessing.next(true);
    this._bsApi
      .post<CardDetailsPayload>(this.apiName, '/accounts/card', { accountId: this.accountId })
      .subscribe({
        next: (resp) => {
          this._shipProcessing.next(false);
          if (resp.result.code === GenericResponseCode.SERVICE_OK) {
            this._spendingAccountSvc.getSpendingAccounts(true);
            this._router.navigate(['accounts', 'spending', 'debit-card', 'digital-confirmation']);
          } else {
            this._globalModalSvc.createGenericErrorModal(
              'ship_debit_card_failed',
              () => this._globalModalSvc.clearModal(),
              'OK',
              () => {
                this._router.navigate(['accounts', 'spending', 'dashboard']);
                this._globalModalSvc.clearModal();
              }
            );
          }
        },
        error: () => {
          this._shipProcessing.next(false);
          this._globalModalSvc.createGenericErrorModal(
            'ship_debit_card_failed',
            () => this._globalModalSvc.clearModal(),
            'OK',
            () => {
              this._router.navigate(['accounts', 'spending', 'dashboard']);
              this._globalModalSvc.clearModal();
            }
          );
        },
      });
  }

  private _showOrderPhysicalCardRetryModal(retry: boolean = true): void {
    this._globalModalSvc.createGenericErrorModal(
      'order_physical_card_failed',
      () => {
        if (retry) {
          this._globalModalSvc.clearModal();
          this.sendPhysicalCard();
        } else {
          this._router.navigate(['accounts', 'spending', 'dashboard']);
          this._globalModalSvc.clearModal();
        }
      },
      retry ? 'TRY_AGAIN' : 'OK',
      () => {
        this._router.navigate(['accounts', 'spending', 'dashboard']);
        this._globalModalSvc.clearModal();
      }
    );
  }

  changePin(pinNumber: string, flow?: undefined | string) {
    this._changePinProcessing$.next(true);
    const changePinToast = flow && flow === 'activate' ? 'DEBIT_CARD_ACTIVATED' : 'DEBIT_CARD_CHANGE_PIN_SAVED';

    this._runWithCardId((card) => {
      this._bsApi
        .post<ChangePinPayload>(this.apiName, '/accounts/card/reset-pin', {
          cardId: card.cardId,
          newPin: pinNumber,
        })
        .subscribe((resp) => {
          if (resp.result.code === GenericResponseCode.SERVICE_OK) {
            this.getCurrentCard(true);
            this._toastService.infoBlue(this._translateService.instant(changePinToast));
            this._router.navigate(['accounts', 'spending', 'dashboard']);
          } else {
            this._globalModalSvc.createGenericErrorModal(
              'change_pin',
              () => {
                this._globalModalSvc.clearModal();
              },
              'TRY_AGAIN',
              () => {
                this.getCurrentCard(true);
                this._router.navigate(['accounts', 'spending', 'dashboard']);
                this._globalModalSvc.clearModal();
              }
            );
          }
          this._changePinProcessing$.next(false);
        });
    });
  }

  toggleLockCard(unlocked: boolean) {
    this._globalModalSvc.createModal({
      title: unlocked ? 'LOCK_CARD_TITLE' : 'UNLOCK_CARD_TITLE',
      contentKey: unlocked ? 'LOCK_CARD_DESC' : 'UNLOCK_CARD_DESC',
      displayCloseButton: true,
      eventName: unlocked ? 'lock_card' : 'unlock_card',
      isError: false,
      size: 'medium',
      loadingAfterCta: true,
      ctaKey: unlocked ? 'LOCK_CARD_CTA' : 'UNLOCK_CARD_CTA',
      secondaryCtaKey: 'BUTTON_CANCEL',
      secondaryCtaAction: () => {
        this._cardLockStatus$.next(unlocked);
        setTimeout(() => this._cardLockStatus$.next(!unlocked), 0);
        this._globalModalSvc.clearModal();
      },
      ctaAction: () => {
        this.lockOrUnlockCard(unlocked);
      },
      closeAction: () => {
        this._cardLockStatus$.next(unlocked);
        setTimeout(() => this._cardLockStatus$.next(!unlocked), 0);
        this._globalModalSvc.clearModal();
      }
    });
  }

  lockOrUnlockCard(unlocked: boolean) {
    const lockStatus = unlocked ? 'lock' : 'unlock';

    this._runWithCardId((card) => {
      this._bsApi
        .patch<LockCardPayload>(this.apiName, `/accounts/card/${lockStatus}`, {
          cardId: card.cardId,
        })
        .subscribe((resp) => {
          if (resp.result.code === GenericResponseCode.SERVICE_OK) {
            this._cardLockStatus$.next(unlocked);
            this._lockCardToggleLabelKey$.next(unlocked ? 'LOCK_CARD_TOGGLE_ON' : 'LOCK_CARD_TOGGLE_OFF');
            this._toastService.success(
              this._translateService.instant(unlocked ? this.cardLockSuccessToast : this.cardUnlockSuccessToast)
            );
            this._spendingAccountSvc.getSpendingAccounts(true);
            this._globalModalSvc.clearModal();
          } else {
            this._cardLockStatus$.next(unlocked);
            setTimeout(() => this._cardLockStatus$.next(!unlocked), 0);
            this._toastService.info(
              this._translateService.instant(unlocked ? this.cardLockFailedToast : this.cardUnlockFailedToast)
            );
            this._globalModalSvc.clearModal();
          }
        });
    });
  }

  showDebitCardHelpModal() {
    if (this._debitCardHelpModalShown) return;
    this._debitCardHelpModalShown = true;
    this.cardLockStatus$
      .pipe(take(1))
      .subscribe({
        next: (locked) => {
          this._cardLockStatus$.next(!locked);
          setTimeout(() => this._cardLockStatus$.next(locked), 0);
          this._globalModalSvc.createModal({
            title: 'DEBIT_CARD_HELP_HALFSHEET_TITLE',
            contentKey: 'DEBIT_CARD_HELP_HALFSHEET_DESC',
            displayCloseButton: true,
            eventName: 'debit_card_help',
            isError: false,
            size: 'medium',
            ctaKey: 'NEED_MORE_HELP_CTA1',
            ctaAction: () => {
              this._cardLockStatus$.next(locked);
              this._bsChatSvc.showChat();
              this._debitCardHelpModalShown = false;
            },
            closeAction: () => {
              this._cardLockStatus$.next(locked);
              this._debitCardHelpModalShown = false;
              this._globalModalSvc.clearModal();
            },
          });
        },
      });
  }
}
